import React, { ReactElement } from 'react'
import { FieldArray, useFormikContext } from 'formik'
import { FormattedMessage, useIntl, defineMessages } from 'react-intl'
import styled from 'styled-components'
import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { InputDate } from 'components/v2/InputDate'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import { CloseIcon } from 'components/Icons'
import { isAfter, isBefore, startOfDay } from 'date-fns'
import { IPeakSettingsFormValues } from './types'
import Heading from 'components/v2/Heading'
import { isEmpty } from 'lodash'

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const IconDelete = styled.div`
  position: absolute;
  right: -10px;
  top: 45px;
  transform: translate(100%, -50%);
`

const StyledRow = styled(Row)`
  position: relative;
`

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
  width: 12px;
  height: 12px;
  border: 1px solid black;
  border-radius: 50%;
`

const messages = defineMessages({
  title: {
    id: 'services.peakSettings.title',
    defaultMessage: 'Peak'
  },
  add: {
    id: 'services.peakSettings.add',
    defaultMessage: 'Add'
  },
  startDatePlaceholder: {
    id: 'services.peakSettings.startDate.placeholder',
    defaultMessage: 'Starts On'
  },
  endDatePlaceholder: {
    id: 'services.peakSettings.endDate.placeholder',
    defaultMessage: 'Ends On'
  },
  startDateLabel: {
    id: 'services.peakSettings.startDate.label',
    defaultMessage: 'Starting date'
  },
  endDateLabel: {
    id: 'services.peakSettings.endDate.label',
    defaultMessage: 'Ending date'
  }
})

const PeakSettingsField = ({ canDelete = true }: { canDelete?: boolean }): ReactElement => {
  const intl = useIntl()
  const { values, touched, errors, setFieldValue } = useFormikContext<IPeakSettingsFormValues>()

  return (
    <FieldArray
      name="peakSettings"
      render={(arrayHelpers): React.ReactElement => {
        return (
          <>
            <FlexContainer>
              <Heading.H4>
                <FormattedMessage {...messages.title} />
              </Heading.H4>
              <Button
                type="button"
                kind="secondary"
                onClick={(): void =>
                  arrayHelpers.push({
                    startAt: undefined,
                    endAt: undefined
                  })
                }
              >
                <FormattedMessage {...messages.add} />
              </Button>
            </FlexContainer>
            {values.peakSettings.map((dateRange, index) => {
              const isNewItem = isEmpty(dateRange.id)
              const isShowingDeleteButton = isNewItem ? values.peakSettings.length > 1 : canDelete

              return (
                <StyledRow key={index} noMargin>
                  <Column width="50%">
                    <InputDate
                      disabled={!isNewItem && isBefore(startOfDay(dateRange.startAt as Date), startOfDay(new Date()))}
                      autoComplete="off"
                      id="startAt"
                      intl={intl}
                      value={dateRange.startAt}
                      placeholder={intl.formatMessage(messages.startDatePlaceholder)}
                      onChange={(value): void => {
                        if (!value) return
                        setFieldValue(`peakSettings.${index}.startAt`, value)

                        if (dateRange.endAt && isBefore(dateRange.endAt, value)) {
                          setFieldValue(`peakSettings.${index}.endAt`, value)
                        }
                      }}
                      disabledDays={{
                        before: new Date()
                      }}
                      touched={Boolean(touched.peakSettings?.[index]?.startAt)}
                      label={<FormattedMessage {...messages.startDateLabel} />}
                    />
                  </Column>

                  <Column width="50%">
                    <InputDate
                      disabled={!isNewItem && isAfter(startOfDay(new Date()), startOfDay(dateRange.endAt as Date))}
                      autoComplete="off"
                      id="endAt"
                      intl={intl}
                      value={dateRange.endAt}
                      placeholder={intl.formatMessage(messages.endDatePlaceholder)}
                      onChange={(value): void => {
                        if (!value) return
                        setFieldValue(`peakSettings.${index}.endAt`, value)

                        if (dateRange.startAt && isBefore(value, dateRange.startAt)) {
                          setFieldValue(`peakSettings.${index}.startAt`, value)
                        }
                      }}
                      disabledDays={{
                        before: new Date()
                      }}
                      touched={Boolean(touched.peakSettings?.[index]?.endAt)}
                      label={<FormattedMessage {...messages.endDateLabel} />}
                    />
                  </Column>
                  <ErrorMessage>{errors.peakSettings?.[index] ?? ''}</ErrorMessage>
                  {isShowingDeleteButton ? (
                    <IconDelete>
                      <div
                        onClick={(): void => {
                          if (index === 0 && values.peakSettings.length === 1) {
                            setFieldValue(`peakSettings.${index}.startAt`, undefined)
                            setFieldValue(`peakSettings.${index}.endAt`, undefined)
                          } else {
                            arrayHelpers.remove(index)
                          }
                        }}
                      >
                        <StyledCloseIcon />
                      </div>
                    </IconDelete>
                  ) : null}
                </StyledRow>
              )
            })}
          </>
        )
      }}
    />
  )
}

export default PeakSettingsField
